/**
 * Curve and gradient value types shared by the editor's CurveField and
 * GradientField. They mirror the engine's types in
 * engine/crates/prism_core/src/particles.rs (`Curve`, `MinMaxCurve`,
 * `Gradient`, `MinMaxGradient`) and evaluate exactly like them, so any
 * system that stores these JSON shapes can use the same fields. The plain
 * curve / gradient math lives in lib/curves.ts (shared with the reusable
 * CurveEditor / GradientEditor controls).
 */

import {
  clamp01,
  cleanGradient,
  cssColor,
  evalCurve,
  evalGradient,
  gradientBetween,
  gradientCss,
  isCurveKeys,
  isGradient,
  r3,
  rgba,
  WHITE,
  type CurveKeys,
  type Gradient,
  type Rgba,
} from '../../../lib/curves.js';

export {
  clamp01,
  cleanGradient,
  cssColor,
  evalCurve,
  evalGradient,
  gradientBetween,
  gradientCss,
  isCurveKeys,
  isGradient,
  r3,
  rgba,
  WHITE,
  type CurveKeys,
  type Gradient,
  type Rgba,
};

/**
 * Unity's MinMaxCurve: constant, random between two constants, a curve or
 * random between two curves (`smooth`: cubic instead of linear between keys).
 */
export type MinMaxCurve =
  | number
  | [number, number]
  | { curve: CurveKeys; multiplier?: number; smooth?: boolean }
  | { curves: [CurveKeys, CurveKeys]; multiplier?: number; smooth?: boolean };

export type CurveMode = 'constant' | 'random' | 'curve' | 'randomCurves';
export const CURVE_MODES: readonly CurveMode[] = ['constant', 'random', 'curve', 'randomCurves'];
export const CURVE_MODE_LABELS: Record<CurveMode, string> = {
  constant: 'Constant',
  random: 'Random between two constants',
  curve: 'Curve',
  randomCurves: 'Random between two curves',
};

/** Unity's MinMaxGradient. */
export type MinMaxGradient =
  | number[]
  | { random: [number[], number[]] }
  | { gradient: Gradient; random?: boolean }
  | { gradients: [Gradient, Gradient] };

export type GradientMode = 'color' | 'gradient' | 'randomColors' | 'randomGradients' | 'randomColor';
export const GRADIENT_MODES: readonly GradientMode[] = ['color', 'gradient', 'randomColors', 'randomGradients', 'randomColor'];
export const GRADIENT_MODE_LABELS: Record<GradientMode, string> = {
  color: 'Color',
  gradient: 'Gradient',
  randomColors: 'Random between two colors',
  randomGradients: 'Random between two gradients',
  randomColor: 'Random color (from gradient)',
};

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isObj = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v);

/** Smooth interpolation flag of a curve-mode MinMaxCurve. */
export function curveSmooth(v: unknown): boolean {
  return isObj(v) && v.smooth === true;
}

/** The value with smooth interpolation on / off (curve modes only; others unchanged). */
export function withCurveSmooth(v: unknown, smooth: boolean): unknown {
  if (!isObj(v) || (v.curve === undefined && v.curves === undefined)) return v;
  const next: Record<string, unknown> = { ...v };
  if (smooth) next.smooth = true;
  else delete next.smooth;
  return next;
}

/**
 * Value of a MinMaxCurve at curve time `t` for the per-particle random `r`
 * (the engine's `MinMaxCurve::eval`). Unknown shapes evaluate to 0.
 */
export function evalMinMaxCurve(v: unknown, t: number, r = 0.5): number {
  const mode = curveMode(v);
  if (mode === 'constant') return isNum(v) ? v : 0;
  if (mode === 'random') {
    const [a, b] = v as [number, number];
    return a + (b - a) * r;
  }
  const obj = v as { curve?: CurveKeys; curves?: [CurveKeys, CurveKeys]; multiplier?: number; smooth?: boolean };
  const m = isNum(obj.multiplier) ? obj.multiplier : 1;
  const smooth = obj.smooth === true;
  if (mode === 'curve') return evalCurve(obj.curve!, t, smooth) * m;
  if (mode === 'randomCurves') {
    const a = evalCurve(obj.curves![0], t, smooth);
    const b = evalCurve(obj.curves![1], t, smooth);
    return (a + (b - a) * r) * m;
  }
  return 0;
}

/** The mode of a MinMaxCurve value, or null when it is not one (absent = constant). */
export function curveMode(v: unknown): CurveMode | null {
  if (v === undefined || isNum(v)) return 'constant';
  if (Array.isArray(v) && v.length === 2 && isNum(v[0]) && isNum(v[1])) return 'random';
  if (isObj(v)) {
    if (isCurveKeys(v.curve) && v.curves === undefined) return 'curve';
    if (Array.isArray(v.curves) && v.curves.length === 2 && v.curves.every(isCurveKeys) && v.curve === undefined) return 'randomCurves';
  }
  return null;
}

const keysRange = (keys: CurveKeys): [number, number] =>
  keys.length === 0 ? [0, 0] : [Math.min(...keys.map((k) => k[1])), Math.max(...keys.map((k) => k[1]))];

/** Converts a MinMaxCurve to another mode, keeping its values as well as possible. */
export function convertCurve(v: unknown, to: CurveMode): MinMaxCurve {
  const out = convertCurveValue(v, to);
  // Curve modes keep the smooth flag.
  return curveSmooth(v) && (to === 'curve' || to === 'randomCurves') ? (withCurveSmooth(out, true) as MinMaxCurve) : out;
}

function convertCurveValue(v: unknown, to: CurveMode): MinMaxCurve {
  const from = curveMode(v);
  const obj = isObj(v) ? v : {};
  const mult = isNum(obj.multiplier) ? obj.multiplier : 1;
  // Representative [low, high] of the current value.
  let lo = 0;
  let hi = 0;
  if (from === 'constant') lo = hi = isNum(v) ? v : 0;
  else if (from === 'random') [lo, hi] = v as [number, number];
  else if (from === 'curve') [lo, hi] = keysRange(obj.curve as CurveKeys).map((x) => x * mult) as [number, number];
  else if (from === 'randomCurves') {
    const [a, b] = obj.curves as [CurveKeys, CurveKeys];
    lo = keysRange(a)[0] * mult;
    hi = keysRange(b)[1] * mult;
  }
  switch (to) {
    case 'constant':
      return r3(from === 'random' || from === 'randomCurves' ? (lo + hi) / 2 : hi);
    case 'random':
      return [r3(Math.min(lo, hi)), r3(Math.max(lo, hi))];
    case 'curve': {
      if (from === 'randomCurves') return withMultiplier({ curve: (obj.curves as [CurveKeys, CurveKeys])[1] }, mult);
      if (from === 'random') return { curve: [[0, r3(lo)], [1, r3(hi)]] };
      return hi !== 0 ? withMultiplier({ curve: [[0, 1], [1, 1]] }, hi) : { curve: [[0, 0], [1, 1]] };
    }
    case 'randomCurves': {
      if (from === 'curve') {
        const c = obj.curve as CurveKeys;
        return withMultiplier({ curves: [c.map(([t, x]) => [t, r3(x * 0.5)] as [number, number]), c] }, mult);
      }
      if (from === 'random') return { curves: [[[0, r3(lo)], [1, r3(lo)]], [[0, r3(hi)], [1, r3(hi)]]] };
      const m = hi !== 0 ? hi : 1;
      return withMultiplier({ curves: [[[0, 0], [1, 0]], [[0, 1], [1, 1]]] }, m);
    }
  }
}

function withMultiplier(v: { curve: CurveKeys } | { curves: [CurveKeys, CurveKeys] }, m: number): MinMaxCurve {
  return m === 1 ? v : { ...v, multiplier: r3(m) };
}

/** Sorts keys by time; returns the new index of `tracked` (a key object in `keys`). */
export function sortKeys<K extends number[]>(keys: K[], tracked?: K): { keys: K[]; index: number } {
  const sorted = [...keys].sort((a, b) => (a[0] ?? 0) - (b[0] ?? 0));
  return { keys: sorted, index: tracked ? sorted.indexOf(tracked) : -1 };
}

// ---------------------------------------------------------------------------
// Gradients

/**
 * Color of a MinMaxGradient at time `t` for the per-particle random `r`
 * (the engine's `MinMaxGradient::eval`). Unknown shapes are white.
 */
export function evalMinMaxGradient(v: unknown, t: number, r = 0.5): Rgba {
  const mode = gradientMode(v);
  const obj = (isObj(v) ? v : {}) as { random?: unknown; gradient?: Gradient; gradients?: [Gradient, Gradient] };
  const lerp = (a: Rgba, b: Rgba): Rgba => [0, 1, 2, 3].map((i) => a[i]! + (b[i]! - a[i]!) * r) as Rgba;
  switch (mode) {
    case 'color':
      return rgba(v ?? WHITE);
    case 'randomColors':
      return lerp(rgba((obj.random as unknown[])[0]), rgba((obj.random as unknown[])[1]));
    case 'gradient':
      return evalGradient(obj.gradient, t);
    case 'randomColor':
      return evalGradient(obj.gradient, r);
    case 'randomGradients':
      return lerp(evalGradient(obj.gradients![0], t), evalGradient(obj.gradients![1], t));
    default:
      return WHITE;
  }
}

/** The mode of a MinMaxGradient value, or null when it is not one (absent = color). */
export function gradientMode(v: unknown): GradientMode | null {
  if (v === undefined) return 'color';
  if (Array.isArray(v)) return v.length >= 3 && v.length <= 4 && v.every(isNum) ? 'color' : null;
  if (!isObj(v)) return null;
  if (Array.isArray(v.random) && v.random.length === 2) return 'randomColors';
  if (isGradient(v.gradient)) return v.random === true ? 'randomColor' : 'gradient';
  if (Array.isArray(v.gradients) && v.gradients.length === 2 && v.gradients.every(isGradient)) return 'randomGradients';
  return null;
}

/** Converts a MinMaxGradient to another mode, keeping its colors as well as possible. */
export function convertGradient(v: unknown, to: GradientMode): MinMaxGradient {
  const from = gradientMode(v);
  const obj = isObj(v) ? v : {};
  let pair: [Gradient, Gradient];
  if (from === 'randomColors') {
    const a = rgba((obj.random as unknown[])[0]);
    const b = rgba((obj.random as unknown[])[1]);
    pair = [gradientBetween(a, a), gradientBetween(b, b)];
  } else if (from === 'gradient' || from === 'randomColor') pair = [obj.gradient as Gradient, obj.gradient as Gradient];
  else if (from === 'randomGradients') pair = obj.gradients as [Gradient, Gradient];
  else {
    const c = rgba(v ?? WHITE);
    pair = [gradientBetween(c, c), gradientBetween(c, c)];
  }
  const first = evalGradient(pair[0], 0);
  const last = evalGradient(pair[1], from === 'randomColors' ? 0 : 1);
  switch (to) {
    case 'color':
      return first.map(r3);
    case 'randomColors':
      return { random: [first.map(r3), last.map(r3)] };
    case 'gradient':
    case 'randomColor': {
      const gradient = from === 'randomColors' ? gradientBetween(first, last) : pair[1];
      return to === 'randomColor' ? { gradient, random: true } : { gradient };
    }
    case 'randomGradients':
      return { gradients: [pair[0], pair[1]] };
  }
}
